/**
 * PM-WF-01 → M3C-04 — the project-side workflow picker (the Workflow panel,
 * the bulk "Set workflow…").
 *
 * The options are the tenant's OWN active workflows from workflows/v1
 * (`useWorkflows`), each by its latest active VERSION id and the server's
 * name (M3C-03: a project pins a version, DI-D61). A shared injection node is
 * never offered: a project is the owner's and a node shared WITH the tenant
 * takes a drop only (Christian 2026-10-12). The tenant's default is the
 * version the server marks `isDefault` — the FE spells no workflow key (lock
 * `no-fe-registry-of-keys`; the PM-WF-01 `DEFAULT_PROJECT_WORKFLOW` literal
 * is gone).
 *
 * The create wizard picks its version at step 0 (`PickWorkflowStep`), not
 * here; an edit changes the version only through the Workflow panel
 * (DI-D9 c3).
 */
import type { ReactElement } from "react";
import {
  Alert,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
} from "@mui/material";
import {
  defaultWorkflowOf,
  injectionFactsOf,
  injectionKindWords,
  ownWorkflows,
  useWorkflows,
  workflowByVersionId,
  type Workflow,
} from "../workflows/api/workflows.js";

/** The version a new project starts with: the tenant's default, else none. */
export function defaultProjectVersion(
  list: readonly Workflow[] | undefined,
): string {
  return defaultWorkflowOf(list)?.latestVersion.versionId ?? "";
}

/**
 * The name shown for a project's version: the workflow's name (own list),
 * else the BE's `workflowLabel` (the legacy key's registry label — a project
 * that pins no version, or a version no longer active), else the key.
 */
export function projectWorkflowLabel(
  versionId: string | null,
  list: readonly Workflow[] | undefined,
  fallbackLabel?: string | null,
  legacyKey?: string | null,
): string {
  const w = workflowByVersionId(list, versionId);
  if (w) return w.name;
  return fallbackLabel ?? legacyKey ?? "—";
}

/** The kind words of a version's injection node (its served facts). */
export function projectWorkflowMeaning(
  versionId: string | null,
  list: readonly Workflow[] | undefined,
): string | null {
  const w = workflowByVersionId(list, versionId);
  if (!w) return null;
  return injectionKindWords(injectionFactsOf(w.latestVersion));
}

/** Friendly copy for the BE's workflow refusals (422 reasons). */
export function workflowErrorMessage(err: unknown): string {
  const reason = (err as { body?: { reason?: unknown } } | null)?.body?.reason;
  if (reason === "workflow_not_available") {
    return "This workflow is not available to your company (any more) — pick another or ask your tenant admin.";
  }
  if (reason === "unknown_workflow") {
    return "This workflow does not exist (any more).";
  }
  const status = (err as { status?: unknown } | null)?.status;
  if (status === 404) return "The project was not found.";
  if (status === 403) return "You are not allowed to change the workflow.";
  return "Couldn't save the workflow — try again.";
}

export interface WorkflowSelectProps {
  /** The picked VERSION id ("" = none). */
  value: string;
  onChange: (versionId: string) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** Test/aria id suffix. */
  id?: string;
}

export function WorkflowSelect({
  value,
  onChange,
  label = "Workflow",
  required = false,
  error = false,
  helperText,
  disabled = false,
  id = "project-workflow",
}: WorkflowSelectProps): ReactElement {
  const workflows = useWorkflows();
  const list = ownWorkflows(workflows.data);

  if (workflows.isPending && workflows.fetchStatus !== "idle") {
    return (
      <Skeleton variant="rounded" height={56} aria-label="Loading workflows" />
    );
  }
  if (workflows.isError) {
    return (
      <Alert severity="error" variant="outlined">
        Couldn&apos;t load the workflows your company can use.
      </Alert>
    );
  }
  if (list.length === 0) {
    return (
      <Alert severity="warning" variant="outlined">
        Your company has no workflow yet — ask your tenant admin.
      </Alert>
    );
  }

  const meaning = value ? projectWorkflowMeaning(value, workflows.data) : null;
  const labelId = `${id}-label`;
  return (
    <FormControl
      fullWidth
      required={required}
      error={error}
      disabled={disabled}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={id}
        label={label}
        value={
          list.some((w) => w.latestVersion.versionId === value) ? value : ""
        }
        onChange={(e) => {
          onChange(e.target.value);
        }}
        inputProps={{ "aria-label": label }}
      >
        {list.map((w) => (
          <MenuItem
            key={w.latestVersion.versionId}
            value={w.latestVersion.versionId}
          >
            {w.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {helperText ??
          meaning ??
          "How this project's data acquisitions are handled."}
      </FormHelperText>
    </FormControl>
  );
}
